"use client";

import { cn } from "@ui/lib";
import { Volume2Icon, VolumeXIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";

/**
 * 进视口自动播、默认静音、只有一个静音切换按钮的样片。
 * 文案（aria-label）由服务端传进来，这里不读 i18n。
 */
export function GrokClip({
	src,
	poster,
	muteLabel,
	unmuteLabel,
	className,
}: {
	src: string;
	poster: string;
	muteLabel: string;
	unmuteLabel: string;
	className?: string;
}) {
	const videoRef = useRef<HTMLVideoElement | null>(null);
	const [muted, setMuted] = useState(true);

	// React 的 muted 属性不一定落到 DOM property 上
	useEffect(() => {
		if (videoRef.current) {
			videoRef.current.muted = muted;
		}
	}, [muted]);

	// 跟着可见性播 / 停，别让整页样片一起拉流量
	useEffect(() => {
		const video = videoRef.current;
		if (!video) {
			return;
		}
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					// 浏览器仍可能拒绝自动播，忽略即可
					video.play().catch(() => undefined);
				} else {
					video.pause();
				}
			},
			{ threshold: 0.25 },
		);
		observer.observe(video);
		return () => observer.disconnect();
	}, []);

	return (
		<div className={cn("relative", className)}>
			<video
				ref={videoRef}
				src={src}
				poster={poster}
				muted
				loop
				playsInline
				preload="none"
				className="size-full object-cover"
			/>
			<button
				type="button"
				onClick={() => setMuted((previous) => !previous)}
				aria-label={muted ? unmuteLabel : muteLabel}
				className="absolute top-3 right-3 flex size-8 items-center justify-center rounded-full bg-black/50 backdrop-blur transition-colors hover:bg-black/70"
			>
				{muted ? (
					<VolumeXIcon className="size-4 text-white" />
				) : (
					<Volume2Icon className="size-4 text-white" />
				)}
			</button>
		</div>
	);
}
